import React, { useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import {
	groupedDefaultComponents,
	defaultGetOptionValue,
	purpleSelectStyles
} from './MultiDropdownGrouped';

/**
 * Async multi-select dropdown that pins selected items at the top of the
 * menu, backed by react-select's `AsyncSelect`
 * (https://react-select.com/async).
 *
 * Grouping happens at render time inside the shared `MenuList` override
 * (see `groupedDefaultComponents`) — *not* inside `loadOptions`. That's
 * intentional: `AsyncSelect` caches `loadOptions` results in a ref keyed by
 * input value and only loads `defaultOptions={true}` once on mount, so any
 * pre-grouping done at load time would go stale the moment `value` changes.
 * Reading `value` from `selectProps` during render keeps the "Selected"
 * group always in sync with the actual selection.
 */
function MultiDropdownGroupedAsync({
	getOptionValue = defaultGetOptionValue,
	selectedGroupLabel = 'Selected',
	availableGroupLabel = 'Options',
	isClearable = true,
	components: extraComponents,
	styles: customStyles,
	usePurpleStyles = false,
	...rest
}) {
	const mergedComponents = useMemo(
		() => ({ ...groupedDefaultComponents, ...(extraComponents || {}) }),
		[extraComponents]
	);

	const mergedStyles = useMemo(() => {
		const mergeStyleFn = (key, base, state) => {
			const next = usePurpleStyles
				? (purpleSelectStyles[key]?.(base, state) ?? base)
				: base;
			return customStyles?.[key] ? customStyles[key](next, state) : next;
		};

		return {
			...customStyles,
			control: (base, state) => mergeStyleFn('control', base, state),
			option: (base, state) => mergeStyleFn('option', base, state),
			multiValue: (base, state) => mergeStyleFn('multiValue', base, state),
			multiValueLabel: (base, state) =>
				mergeStyleFn('multiValueLabel', base, state),
			multiValueRemove: (base, state) =>
				mergeStyleFn('multiValueRemove', base, state)
		};
	}, [customStyles, usePurpleStyles]);

	return (
		<AsyncSelect
			isMulti
			getOptionValue={getOptionValue}
			components={mergedComponents}
			hideSelectedOptions={false}
			// Forwarded onto `selectProps` so the shared `MenuList` override
			// can read the labels for the section headings.
			selectedGroupLabel={selectedGroupLabel}
			availableGroupLabel={availableGroupLabel}
			isClearable={isClearable}
			{...rest}
			styles={mergedStyles}
		/>
	);
}

export default MultiDropdownGroupedAsync;
